"use client";

import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
} from "modules/common/components/Table/TableComponents";
import { ColumnSkeleton } from "modules/common/components/Table/ColumnSkeleton";
import { cn } from "utils/utils";
import { useParams } from "next/navigation";
import type { GameId } from "modules/common/model/models";
import { getGameMetadata } from "modules/common/gameRegistry";
import {
  getSetTableNameColumnWidth,
  SET_TABLE_COLUMN_WIDTHS,
} from "modules/sets/utils/setTableLayout";

/**
 * Column definitions matching SetTable's columns. Widths mirror the real
 * SetTable's `meta.width` percentages so the skeleton has the same layout
 * as the loaded table — preventing a width-shift on hydrate.
 */
interface SetTableSkeletonProps {
  rowCount?: number;
}

/**
 * Suspense fallback skeleton for SetTable.
 * Matches the output of DataTable's TableLoadingSkeleton for SetTable's columns.
 */
export function SetTableSkeleton({ rowCount = 20 }: SetTableSkeletonProps) {
  const params = useParams<{ game: GameId }>();
  const presentation = getGameMetadata(params.game).presentation.setTable;
  const columns = [
    {
      id: "icon",
      header: null,
      className: presentation.showIcon ? undefined : "hidden",
      width: `${SET_TABLE_COLUMN_WIDTHS.icon}%`,
    },
    {
      id: "name",
      header: "Name",
      className: undefined,
      width: getSetTableNameColumnWidth(presentation),
    },
    {
      id: "code",
      header: "Code",
      className: "max-md:hidden",
      width: `${SET_TABLE_COLUMN_WIDTHS.code}%`,
    },
    {
      id: "totalSetSize",
      header: "Cards",
      className: presentation.showTotalSize ? "max-md:hidden" : "hidden",
      width: `${SET_TABLE_COLUMN_WIDTHS.totalSetSize}%`,
    },
    {
      id: "marketCap",
      header: "Market Cap",
      className: "hidden md:table-cell",
      width: `${SET_TABLE_COLUMN_WIDTHS.marketCap}%`,
    },
    {
      id: "type",
      header: "Type",
      className: presentation.showType ? "hidden md:table-cell" : "hidden",
      width: `${SET_TABLE_COLUMN_WIDTHS.type}%`,
    },
    {
      id: "releaseDate",
      header: "Released",
      className: undefined,
      width: `${SET_TABLE_COLUMN_WIDTHS.releaseDate}%`,
    },
  ];

  return (
    <TableContainer
      className={cn(
        "rounded-box border-base-content/20 bg-base-100 shadow-xs relative overflow-y-auto border",
        "relative max-h-[calc(100svh-220px)] overflow-y-auto md:max-h-[57dvh]"
      )}
    >
      <Table className="table-zebra w-full table-fixed">
        <TableHead className="bg-base-100 sticky top-0 z-30">
          <TableRow className="border-none">
            {columns.map((col) => (
              <TableHeader
                key={col.id}
                className={`bg-base-100 text-base-content/70 border-none text-sm font-medium ${col.className ?? ""}`}
                style={{ width: col.width }}
              >
                {col.header}
              </TableHeader>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {Array.from({ length: rowCount }).map((_, i) => (
            <TableRow key={i}>
              {columns.map((col) => (
                <TableCell
                  key={col.id}
                  className={col.className}
                  style={{ width: col.width }}
                >
                  <ColumnSkeleton column={{ id: col.id, header: col.header }} />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
